import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { todayStr } from "@/lib/format";

const REV_PAYMENT = ["UPI", "Bank Transfer", "Cash", "Payment Gateway", "Other"];
const EXP_PAYMENT = ["UPI", "Bank Transfer", "Credit/Debit Card", "Cash", "Other"];

export default function TransactionDialog({ state, onClose, onPick, onSaved }) {
  const open = !!state;
  const choose = state?.choose;
  const type = state?.type;
  const record = state?.record;

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!type) return;
    const endpoint = type === "revenue" ? "/revenue-categories" : "/expense-categories";
    api.get(endpoint).then((r) => setCategories(r.data.filter((c) => c.is_active)));
    if (record) {
      setForm({ ...record });
    } else {
      setForm(
        type === "revenue"
          ? { date: todayStr(), revenue_type: "", description: "", amount: "", payment_mode: "UPI", reference_number: "", notes: "" }
          : { date: todayStr(), category: "", description: "", amount: "", payment_mode: "UPI", vendor: "", invoice_number: "", notes: "" }
      );
    }
  }, [type, record]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const catField = type === "revenue" ? "revenue_type" : "category";
    if (!form.date || !form[catField] || !form.amount) {
      toast.error("Date, category and amount are required");
      return;
    }
    if (Number(form.amount) <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      const base = type === "revenue" ? "/revenues" : "/expenses";
      if (record) await api.put(`${base}/${record.id}`, payload);
      else await api.post(base, payload);
      toast.success(`${type === "revenue" ? "Revenue" : "Expense"} ${record ? "updated" : "added"}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-y-auto" data-testid="transaction-dialog">
        {choose ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading">What do you want to add?</DialogTitle>
              <DialogDescription>Choose the type of transaction to record.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 pt-2">
              <button
                data-testid="choose-revenue-btn"
                onClick={() => onPick("revenue")}
                className="flex flex-col items-center gap-3 rounded-xl border-2 border-emerald-100 bg-emerald-50 p-6 transition-colors hover:border-emerald-300 hover:bg-emerald-100"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500 text-white">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <span className="font-semibold text-emerald-700">Revenue</span>
              </button>
              <button
                data-testid="choose-expense-btn"
                onClick={() => onPick("expense")}
                className="flex flex-col items-center gap-3 rounded-xl border-2 border-rose-100 bg-rose-50 p-6 transition-colors hover:border-rose-300 hover:bg-rose-100"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-500 text-white">
                  <TrendingDown className="h-6 w-6" />
                </div>
                <span className="font-semibold text-rose-700">Expense</span>
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {record ? "Edit" : "Add"} {type === "revenue" ? "Revenue" : "Expense"}
              </DialogTitle>
              <DialogDescription>Enter the transaction details below. Amounts are in ₹ (INR).</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Date *</Label>
                <Input type="date" value={form.date || ""} onChange={(e) => set("date", e.target.value)} data-testid="tx-date-input" />
              </div>
              <div className="grid gap-2">
                <Label>{type === "revenue" ? "Revenue Type *" : "Category *"}</Label>
                <Select
                  value={form[type === "revenue" ? "revenue_type" : "category"] || ""}
                  onValueChange={(v) => set(type === "revenue" ? "revenue_type" : "category", v)}
                >
                  <SelectTrigger data-testid="tx-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Amount (₹) *</Label>
                <Input type="number" min="0" step="0.01" placeholder="0" value={form.amount ?? ""} onChange={(e) => set("amount", e.target.value)} data-testid="tx-amount-input" />
              </div>
              <div className="grid gap-2">
                <Label>Description</Label>
                <Input placeholder="e.g. 10 Seller subscriptions – August" value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="tx-description-input" />
              </div>
              <div className="grid gap-2">
                <Label>Payment Mode</Label>
                <Select value={form.payment_mode || "UPI"} onValueChange={(v) => set("payment_mode", v)}>
                  <SelectTrigger data-testid="tx-payment-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(type === "revenue" ? REV_PAYMENT : EXP_PAYMENT).map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {type === "revenue" ? (
                <div className="grid gap-2">
                  <Label>Reference Number</Label>
                  <Input value={form.reference_number || ""} onChange={(e) => set("reference_number", e.target.value)} />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Vendor</Label>
                    <Input value={form.vendor || ""} onChange={(e) => set("vendor", e.target.value)} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Invoice No.</Label>
                    <Input value={form.invoice_number || ""} onChange={(e) => set("invoice_number", e.target.value)} />
                  </div>
                </div>
              )}
              <div className="grid gap-2">
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="tx-save-btn">
                {saving ? "Saving..." : record ? "Update" : "Save"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
